import React from 'react'
import {Link} from 'react-router-dom'
import {KTCard, KTCardBody, KTIcon} from '../../../_websoft/helpers'
import TitleComponent from '../../components/TitleComponent/TitleComponent'

type ReportLinkProps = {
  to: string
  title: string
  description: string
  icon: string
  color: string
  validUntil?: string
}

type ReportGroupProps = {
  title: string
  description: string
  children: React.ReactNode
  fullWidth?: boolean
  order?: string
}

const ReportLink: React.FC<ReportLinkProps> = ({to, title, description, icon, color, validUntil}) => (
  <Link to={to} className='text-decoration-none'>
    <div className='d-flex align-items-center rounded border border-gray-300 p-5 h-100 bg-hover-light'>
      <div className='symbol symbol-45px me-4'>
        <span className={`symbol-label bg-light-${color}`}>
          <KTIcon iconName={icon} className={`fs-2 text-${color}`} />
        </span>
      </div>
      <div className='flex-grow-1'>
        <div className='fw-bolder fs-4 text-gray-900 mb-1'>{title}</div>
        <div className='text-muted fs-7'>{description}</div>
        {validUntil && (
          <div className='text-warning fw-bold fs-7 mt-2'>Važi do: {validUntil}</div>
        )}
      </div>
      <KTIcon iconName='arrow-right' className='fs-2 text-gray-400 ms-3' />
    </div>
  </Link>
)

const ReportGroup: React.FC<ReportGroupProps> = ({title, description, children, fullWidth = false, order = ''}) => (
  <div className={`${fullWidth ? 'col-12' : 'col-xl-6'} ${order}`}>
    <div className='card border border-gray-300 h-100'>
      <div className='card-header border-0 pt-7 pb-0'>
        <div>
          <h3 className='card-title fs-3 fw-bolder text-gray-900 mb-2'>{title}</h3>
          <div className='text-muted fw-semibold fs-7'>{description}</div>
        </div>
      </div>
      <div className='card-body'>
        <div className='row g-4'>{children}</div>
      </div>
    </div>
  </div>
)

const Izvjestaji: React.FC = () => {
  return (
    <KTCard className='mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-7'>
        <div>
          <div className='card-title mb-2'>
            <TitleComponent name='Izvjestaji' />
          </div>
          <div className='text-muted fw-semibold fs-6'>Odaberite izvjestaj koji zelite otvoriti.</div>
        </div>
      </div>

      <KTCardBody className='border-top p-9'>
        <div className='row g-6'>
          <ReportGroup
            title='Kontrola i nalozi'
            description='Operativni izvjestaji proizvodnje i kontrole kvaliteta.'
            order='order-1'
          >
            <div className='col-12'>
              <ReportLink
                to='detaljan-izvjestaj-naloga-panela'
                title='Nalog - detaljan izvjestaj'
                description='Detaljni podaci po nalogu panela.'
                icon='document'
                color='success'
              />
            </div>
            <div className='col-md-6'>
              <ReportLink
                to='dnevni-izvjestaj-panela'
                title='KK - dnevni izvjestaj'
                description='Dnevni pregled kontrole kvaliteta.'
                icon='calendar-8'
                color='info'
              />
            </div>
            <div className='col-md-6'>
              <ReportLink
                to='periodicni-izvjestaj-panela'
                title='KK - periodicni izvjestaj'
                description='Pregled kontrole kvaliteta po periodu.'
                icon='calendar'
                color='warning'
              />
            </div>
          </ReportGroup>

          <ReportGroup
            title='Ostali izvjestaji'
            description='Dodatni pregledi i izvjestaji za panele.'
            order='order-2'
          >
            <div className='col-md-6'>
              <ReportLink
                to='glavni-izvjestaj'
                title='Glavni izvjestaj'
                description='Glavni pregled proizvodnje panela.'
                icon='chart-simple'
                color='primary'
              />
            </div>
            <div className='col-md-6'>
              <ReportLink
                to='glavni-izvjestaj-v2'
                title='Glavni izvjestaj V2'
                description='Stara verzija glavnog izvjestaja.'
                icon='chart-simple-3'
                color='primary'
                validUntil='07/2024'
              />
            </div>
            <div className='col-md-6'>
              <ReportLink
                to='paneli-kolicine-websoft-fmk'
                title='FMK - Websoft kolicine'
                description='Kolicine panela iz FMK sistema.'
                icon='calculator'
                color='info'
              />
            </div>
            <div className='col-md-6'>
              <ReportLink
                to='otprema-kamiona'
                title='Otprema kamiona'
                description='Pregled otpreme, cekanja i utovara.'
                icon='truck'
                color='dark'
              />
            </div>
          </ReportGroup>

          <ReportGroup
            title='Analitika proizvodnje'
            description='Pregledi kolicina prema kupcu, drzavi i komercijalisti.'
            fullWidth
            order='order-3'
          >
            <div className='col-xl-4 col-md-6'>
              <ReportLink
                to='izvjestaj-po-kupcu-drzavi'
                title='Izvjestaj po kupcu / drzavi'
                description='Poredjenje proizvodnje po kupcima i drzavama.'
                icon='people'
                color='success'
              />
            </div>
            <div className='col-xl-4 col-md-6'>
              <ReportLink
                to='izvjestaj-po-drzavi'
                title='Izvjestaj po drzavi'
                description='Pregled proizvodnje i detalja po drzavama.'
                icon='geolocation'
                color='info'
              />
            </div>
            <div className='col-xl-4 col-md-6'>
              <ReportLink
                to='izvjestaj-po-komercijalisti'
                title='Izvjestaj po komercijalisti'
                description='Analiza proizvodnje po komercijalistima.'
                icon='briefcase'
                color='primary'
              />
            </div>
            <div className='col-12'>
              <ReportLink
                to='prosjecna-debljina-panela'
                title='Prosjecna debljina panela'
                description='Prosječna debljina završenih panela po izolaciji i mjesecu, ponderisana proizvedenom količinom.'
                icon='chart-simple'
                color='warning'
              />
            </div>
          </ReportGroup>
        </div>
      </KTCardBody>
    </KTCard>
  )
}

export default Izvjestaji
